/**
 * Prompt Data Adapter
 * Converts Supabase prompt data to local Prompt interface format
 */

import { 
  Prompt, 
  NonprofitCategory, 
  PromptType, 
  PromptDifficulty,
  PromptVariable 
} from '@/types/PromptLibrary';
import { SupabasePrompt } from './promptLibraryService';
import { MockDataGenerator } from './mockDataGenerator';

export class PromptDataAdapter {
  /**
   * Convert Supabase difficulty to PromptDifficulty
   */
  static mapDifficulty(difficulty: string | null | undefined): PromptDifficulty {
    if (!difficulty || typeof difficulty !== 'string') {
      return PromptDifficulty.BEGINNER; // Default fallback
    }
    
    switch (difficulty.toLowerCase()) {
      case 'beginner': return PromptDifficulty.BEGINNER;
      case 'intermediate': return PromptDifficulty.INTERMEDIATE;
      case 'advanced': return PromptDifficulty.ADVANCED;
      case 'expert': return PromptDifficulty.ADVANCED;  // Map expert to advanced
      case 'senior': return PromptDifficulty.SENIOR;
      default: return PromptDifficulty.BEGINNER;
    }
  }

  /**
   * Convert Supabase category to NonprofitCategory
   */
  static mapCategory(category: string | null | undefined): NonprofitCategory {
    if (!category || typeof category !== 'string') {
      return NonprofitCategory.COMMUNICATIONS; // Default fallback
    }
    
    switch (category.toLowerCase()) {
      case 'grant_writing': return NonprofitCategory.GRANT_WRITING;
      case 'fundraising': return NonprofitCategory.FUNDRAISING;
      case 'communications': return NonprofitCategory.COMMUNICATIONS;
      case 'program_management': return NonprofitCategory.PROGRAM_MANAGEMENT;
      case 'marketing': return NonprofitCategory.MARKETING;
      case 'data_analysis': return NonprofitCategory.DATA_ANALYSIS;
      case 'research': return NonprofitCategory.RESEARCH;
      case 'brainstorming': return NonprofitCategory.BRAINSTORMING;
      default: return NonprofitCategory.COMMUNICATIONS;
    }
  }

  /**
   * Convert Supabase category to PromptType
   */
  static mapType(category: string | null | undefined): PromptType {
    if (!category || typeof category !== 'string') {
      return PromptType.COMMUNICATIONS; // Default fallback
    }
    
    switch (category.toLowerCase()) {
      case 'grant_writing': return PromptType.GRANT_WRITING;
      case 'fundraising': return PromptType.FUNDRAISING;
      case 'communications': return PromptType.COMMUNICATIONS;
      case 'program_management': return PromptType.PROGRAM_MANAGEMENT;
      case 'marketing': return PromptType.MARKETING;
      case 'data_analysis': return PromptType.DATA_ANALYSIS;
      case 'research': return PromptType.RESEARCH;
      case 'brainstorming': return PromptType.BRAINSTORMING;
      default: return PromptType.COMMUNICATIONS;
    }
  }

  /**
   * Parse variables from database jsonb field or extract from content
   * Enhanced with database-driven mock data and variable options
   * PRIORITY: Content extraction takes precedence over database JSON
   */
  static parseVariables(variablesField: any, content: string, mockData?: any, variableOptions?: any): PromptVariable[] {
    // ALWAYS extract variables from content first - this is the source of truth
    const contentVariables = this.extractVariablesFromContent(content);
    
    console.log(`Content extraction found ${contentVariables.length} variables:`, 
      contentVariables.map(v => v.name));
    
    // If we have database variables, merge them with content variables
    if (variablesField) {
      try {
        let dbVariables: any[] = [];
        
        // Handle different possible formats of the jsonb field
        if (Array.isArray(variablesField)) {
          dbVariables = variablesField;
        } else if (typeof variablesField === 'object') {
          // If it's an object, convert to array
          dbVariables = Object.entries(variablesField).map(([key, value]) => ({
            name: key,
            ...(typeof value === 'object' ? value : { defaultValue: value })
          }));
        }
        
        console.log(`Database JSON found ${dbVariables.length} variables:`, 
          dbVariables.map(v => v.name || 'unnamed'));
        
        // Create a map of database variables for quick lookup
        const dbVarMap = new Map();
        dbVariables.forEach((dbVar: any) => {
          const name = dbVar.name || dbVar.id;
          if (name) {
            dbVarMap.set(name, dbVar);
          }
        });
        
        // Enhance content variables with database metadata where available
        return contentVariables.map((contentVar) => {
          const dbVar = dbVarMap.get(contentVar.name);
          const mockValue = mockData?.[contentVar.name];
          const options = variableOptions?.[contentVar.name];
          
          if (dbVar) {
            // Use database metadata to enhance the content variable
            return {
              id: contentVar.id,
              name: contentVar.name,
              label: dbVar.label || contentVar.label,
              type: this.determineVariableType(contentVar.name, options, dbVar.type || contentVar.type),
              required: false, // Removed required validation
              placeholder: mockValue || dbVar.placeholder || contentVar.placeholder,
              defaultValue: mockValue || dbVar.defaultValue || dbVar.default || contentVar.defaultValue,
              options: options || dbVar.options || contentVar.options,
              description: dbVar.description || contentVar.description
            };
          }
          
          // Enhanced with database-driven mock data
          return {
            ...contentVar,
            type: this.determineVariableType(contentVar.name, options, contentVar.type),
            required: false, // Removed required validation
            placeholder: mockValue || contentVar.placeholder,
            defaultValue: mockValue || contentVar.defaultValue,
            options: options || contentVar.options
          };
        });
        
      } catch (error) {
        console.warn('Failed to parse database variables, using content extraction only:', error);
      }
    }
    
    // Enhanced content variables with database-driven mock data
    return contentVariables.map((contentVar) => {
      const mockValue = mockData?.[contentVar.name];
      const options = variableOptions?.[contentVar.name];
      
      return {
        ...contentVar,
        type: this.determineVariableType(contentVar.name, options, contentVar.type),
        required: false, // Removed required validation
        placeholder: mockValue || contentVar.placeholder,
        defaultValue: mockValue || contentVar.defaultValue,
        options: options || contentVar.options
      };
    });
  }
  
  /**
   * Extract variables from prompt content
   * Finds all {{variableName}} patterns and creates variable definitions
   */
  static extractVariablesFromContent(content: string): PromptVariable[] {
    if (!content || typeof content !== 'string') {
      return [];
    }

    // Enhanced regex to capture ALL variable formats:
    // - Uppercase: {{PROGRAM_TYPE}}
    // - Mixed case: {{ProgramType}}
    // - Lowercase: {{program_type}}
    // - With numbers: {{PARTICIPANT_COUNT}}
    const variablePattern = /\{\{([A-Z][A-Z0-9_]*|[a-zA-Z][a-zA-Z0-9_]*)\}\}/g;
    const variables: PromptVariable[] = [];
    const foundVariables = new Set<string>();
    
    let match;
    while ((match = variablePattern.exec(content)) !== null) {
      const variableName = match[1];
      
      if (!foundVariables.has(variableName)) {
        foundVariables.add(variableName);
        
        variables.push({
          id: variableName,
          name: variableName,
          label: this.formatLabel(variableName),
          type: this.inferVariableType(variableName),
          required: false, // Removed required validation
          placeholder: this.generatePlaceholder(variableName),
          defaultValue: this.generateDefaultValue(variableName)
        });
      }
    }

    console.log(`Extracted ${variables.length} variables from content:`, variables.map(v => v.name));
    return variables;
  }

  /**
   * Format variable name to readable label
   */
  static formatLabel(variableName: string): string {
    return variableName
      .toLowerCase() // Convert to lowercase first
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, str => str.toUpperCase()) // Capitalize each word
      .trim();
  }

  /**
   * Infer variable type from name
   */
  static inferVariableType(variableName: string): 'text' | 'textarea' | 'number' | 'date' | 'select' {
    const name = variableName.toLowerCase();
    
    // Number types
    if (name.includes('amount') || name.includes('count') || name.includes('number') || 
        name.includes('quantity') || name.includes('size') || name.includes('limit') ||
        name.includes('goal') || name.includes('target') || name.includes('budget')) {
      return 'number';
    }
    
    // Date types
    if (name.includes('date') || name.includes('deadline') || name.includes('due') ||
        name.includes('start') || name.includes('end') || name.includes('launch')) {
      return 'date';
    }
    
    // Textarea types (longer content)
    if (name.includes('description') || name.includes('content') || name.includes('message') ||
        name.includes('story') || name.includes('narrative') || name.includes('overview') ||
        name.includes('summary') || name.includes('details') || name.includes('bio')) {
      return 'textarea';
    }
    
    // Select types (predefined options)
    if (name.includes('type') || name.includes('level') || name.includes('category') ||
        name.includes('platform') || name.includes('status') || name.includes('priority') ||
        name.includes('stage') || name.includes('method') || name.includes('channel')) {
      return 'select';
    }
    
    return 'text';
  }

  /**
   * Generate placeholder text for variable
   * ENHANCED: Added real placeholders matching CSV data patterns
   */
  static generatePlaceholder(variableName: string): string {
    const name = variableName.toLowerCase();
    
    const placeholders: Record<string, string> = {
      // Board Meeting Communication Strategy specific placeholders
      board_size: '12-member board',
      meeting_frequency: 'Monthly meetings',
      packet_contents: 'Financial statements, committee reports, and strategic updates',
      distribution_timeline: '3 business days before meeting',
      meeting_length: '2.5 hours',
      committee_reports: 'Finance, Program, and Development reports',
      ed_report_topics: 'Program impact metrics and operational highlights',
      strategic_topics: 'Community outreach expansion and fundraising strategy',
      board_chair: 'Sarah Johnson',
      engagement_methods: 'Interactive breakout sessions and member surveys',
      decision_framework: 'Consensus-based decision making with majority vote backup',
      minutes_elements: 'Action items, voting outcomes, and follow-up assignments',
      action_system: 'Digital task tracking with monthly progress updates',
      sensitive_matters: 'Personnel discussions and donor confidentiality',
      urgent_channels: 'Email chain and emergency phone tree',
      new_members: 'Board orientation program and mentorship pairing',
      training_topics: 'Fiduciary responsibility and nonprofit governance',
      retreat_focus: 'Strategic planning and board development',
      document_system: 'Cloud-based file sharing with access controls',
      electronic_voting: 'Secure online voting platform',
      
      // Organization related
      organization_name: 'Hope Community Center',
      organization_type: 'Nonprofit',
      organizationname: 'Hope Community Center',
      organization: 'Hope Community Center',
      organizationtype: 'Nonprofit',
      
      // Program related
      program_type: 'Education Support Program',
      participant_count: '150',
      beneficiary_type: 'students',
      resource_allocation: '$75,000',
      
      // People related
      firstname: 'Sarah',
      lastname: 'Johnson',
      donorname: 'John Smith',
      donor: 'John Smith',
      recipientname: 'Maria Garcia',
      contactname: 'Alex Chen',
      name: 'Jordan Smith',
      
      // Financial
      donationamount: '$500',
      grantamount: '$50,000',
      amount: '$25,000',
      budget: '$100,000',
      goal: '$75,000',
      target: '$200,000',
      
      // Projects and programs
      projecttitle: 'Youth Education Program',
      project: 'Youth Education Program',
      program: 'After School Tutoring',
      campaign: 'Annual Giving Campaign',
      event: 'Community Fundraising Gala',
      initiative: 'Food Security Initiative',
      
      // Contact information
      email: 'contact@example.org',
      phone: '(555) 123-4567',
      website: 'www.example.org',
      
      // Locations
      location: 'Boston, MA',
      city: 'Boston',
      state: 'Massachusetts',
      address: '123 Main Street',
      
      // Organizations and entities
      funder: 'Gates Foundation',
      foundation: 'Community Foundation',
      sponsor: 'Local Business Partners',
      partner: 'Education Alliance',
      
      // Dates (HTML5 date input compatible yyyy-MM-dd format)
      date: '2024-12-31',
      deadline: '2024-11-15',
      startdate: '2024-01-15',
      enddate: '2024-12-30',
      duedate: '2024-06-15',
      campaign_deadline: '2024-12-31',
      matching_deadline: '2024-10-31',
      submission_deadline: '2024-02-15',
      report_deadline: '2024-04-30',
      launch_date: '2024-03-01',
      event_date: '2024-09-15',
      
      // Demographics and targeting
      targetpopulation: 'low-income families',
      audience: 'major donors',
      demographic: 'seniors aged 65+',
      community: 'local residents',
      
      // Communication
      platform: 'Facebook',
      channel: 'Email Newsletter',
      medium: 'Social Media',
      
      // Descriptive
      title: 'Community Impact Report',
      subject: 'Thank You for Your Support',
      topic: 'Education Access',
      theme: 'Building Stronger Communities'
    };

    return placeholders[name] || `Enter ${this.formatLabel(variableName).toLowerCase()}`;
  }

  /**
   * Generate default value for variable
   * Enhanced with comprehensive Board Meeting specific defaults
   */
  static generateDefaultValue(variableName: string): string {
    const name = variableName.toLowerCase();
    
    const defaults: Record<string, string> = {
      // Board Meeting Communication Strategy specific defaults
      board_size: '12-member board',
      meeting_frequency: 'Monthly meetings',
      packet_contents: 'Financial statements, committee reports, and strategic updates',
      distribution_timeline: '3 business days before meeting',
      meeting_length: '2.5 hours',
      committee_reports: 'Finance, Program, and Development reports',
      ed_report_topics: 'Program impact metrics and operational highlights',
      strategic_topics: 'Community outreach expansion and fundraising strategy',
      board_chair: 'Sarah Johnson',
      engagement_methods: 'Interactive breakout sessions and member surveys',
      decision_framework: 'Consensus-based decision making with majority vote backup',
      minutes_elements: 'Action items, voting outcomes, and follow-up assignments',
      action_system: 'Digital task tracking with monthly progress updates',
      sensitive_matters: 'Personnel discussions and donor confidentiality',
      urgent_channels: 'Email chain and emergency phone tree',
      new_members: 'Board orientation program and mentorship pairing',
      training_topics: 'Fiduciary responsibility and nonprofit governance',
      retreat_focus: 'Strategic planning and board development',
      document_system: 'Cloud-based file sharing with access controls',
      electronic_voting: 'Secure online voting platform',
      
      // General nonprofit defaults
      organization_name: 'Hope Community Center',
      program_type: 'Education Support Program',
      participant_count: '150',
      beneficiary_type: 'students',
      resource_allocation: '$75,000',
      organizationname: 'Hope Community Center',
      organization: 'Hope Community Center',
      grantamount: '$50,000',
      amount: '$25,000',
      wordcount: '200',
      wordlimit: '300',
      platform: 'Facebook',
      tonestyle: 'professional',
      tone: 'professional',
      
      // Additional nonprofit context defaults
      mission_statement: 'Empowering underserved communities through education and support services',
      target_population: 'Low-income families and at-risk youth',
      geographic_area: 'Greater Boston metropolitan area',
      annual_budget: '$2.5 million',
      staff_size: '15 full-time staff',
      volunteer_base: '200+ active volunteers',
      founding_year: '2010',
      executive_director: 'Maria Rodriguez',
      board_chair_name: 'David Chen',
      key_programs: 'After-school tutoring, job training, and family support services'
    };

    return defaults[name] || '';
  }

  /**
   * Generate example values for auto-populate functionality
   * Enhanced to prioritize database mock data over generated examples
   */
  static generateExampleValues(variables: PromptVariable[]): Record<string, string> {
    const examples: Record<string, string> = {};
    
    variables.forEach(variable => {
      const name = variable.name.toLowerCase();
      
      // Priority 1: Use placeholder if it contains meaningful mock data (not generic text)
      if (variable.placeholder && 
          !variable.placeholder.startsWith('Enter ') && 
          !variable.placeholder.startsWith('Sample ') &&
          variable.placeholder !== variable.label) {
        examples[variable.name] = variable.placeholder;
      }
      // Priority 2: Use defaultValue if available
      else if (variable.defaultValue) {
        examples[variable.name] = variable.defaultValue;
      }
      // Priority 3: Use first option for select variables
      else if (variable.type === 'select' && variable.options && variable.options.length > 0) {
        examples[variable.name] = variable.options[0];
      }
      // Priority 4: Generate contextual examples
      else {
        switch (variable.type) {
          case 'number':
            if (name.includes('amount') || name.includes('budget')) examples[variable.name] = '75000';
            else if (name.includes('count') || name.includes('participant')) examples[variable.name] = '150';
            else if (name.includes('goal') || name.includes('target')) examples[variable.name] = '50000';
            else examples[variable.name] = '100';
            break;
          case 'date':
            // Generate realistic nonprofit-relevant dates
            if (name.includes('deadline') || name.includes('due')) {
              examples[variable.name] = new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            } else if (name.includes('start')) {
              examples[variable.name] = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            } else if (name.includes('end')) {
              examples[variable.name] = new Date(Date.now() + 120 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            } else {
              examples[variable.name] = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            }
            break;
          case 'textarea':
            examples[variable.name] = `Comprehensive ${variable.label.toLowerCase()} tailored for nonprofit impact and community engagement.`;
            break;
          default:
            examples[variable.name] = variable.placeholder || `Sample ${variable.label}`;
        }
      }
    });
    
    return examples;
  }

  /**
   * Determine the most appropriate variable type based on available options and inference
   */
  static determineVariableType(variableName: string, options?: string[], inferredType?: string): 'text' | 'textarea' | 'number' | 'date' | 'select' {
    // If we have options, it's definitely a select
    if (options && options.length > 1) {
      return 'select';
    }
    
    // Otherwise use the inferred type
    return (inferredType as any) || this.inferVariableType(variableName);
  }

  /**
   * Convert SupabasePrompt to local Prompt format
   * Enhanced with database-driven mock data and variable options
   */
  static convertToLocalPrompt(supabasePrompt: SupabasePrompt): Prompt {
    // Use prompt_text field (actual database field) for content
    const content = supabasePrompt.prompt_text || '';
    
    // Parse mock data and variable options from database
    const mockData = supabasePrompt.mock_data;
    const variableOptions = supabasePrompt.variable_options;
    
    // If no database mock data exists, generate it on the fly
    let generatedData = null;
    if (!mockData || Object.keys(mockData).length === 0) {
      const extractedVars = this.extractVariablesFromContent(content);
      if (extractedVars.length > 0) {
        generatedData = MockDataGenerator.generateMockData(content, extractedVars);
      }
    }
    
    const variables = this.parseVariables(
      supabasePrompt.variables, 
      content, 
      mockData || generatedData?.mockData,
      variableOptions || generatedData?.variableOptions
    );
    
    return {
      id: supabasePrompt.id,
      title: supabasePrompt.title || 'Untitled Prompt',
      description: supabasePrompt.description || '',
      content: content,
      category: this.mapCategory(supabasePrompt.category),
      type: this.mapType(supabasePrompt.category),
      difficulty: this.mapDifficulty(supabasePrompt.difficulty),
      tags: supabasePrompt.tags || [],
      variables: variables,
      stats: {
        usageCount: supabasePrompt.usage_count || 0,
        averageRating: supabasePrompt.user_rating || 4.0,
        totalRatings: Math.floor((supabasePrompt.usage_count || 0) / 10) || 1,
        createdAt: new Date(supabasePrompt.created_at || new Date()),
        updatedAt: new Date(supabasePrompt.updated_at || new Date())
      },
      authorId: 'system',
      authorName: 'Lyra AI',
      isPublic: true,
      isFeatured: supabasePrompt.is_featured || false,
      isActive: supabasePrompt.is_active !== false
    };
  }

  /**
   * Convert multiple SupabasePrompts to local Prompt format
   */
  static convertBulkToLocalPrompts(supabasePrompts: SupabasePrompt[]): Prompt[] {
    return supabasePrompts.map(prompt => this.convertToLocalPrompt(prompt));
  }
}